import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { Info } from '@bibliocommons/base-icons';
import { Link } from '@bibliocommons/base-links';
import TextField from 'app/components/shared/TextField';
import { libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';

import { Select, SelectOption } from 'app/components/shared/Select';
import FormattedHTMLMessage from 'app/components/shared/FormattedHTMLMessage';
import { ModalHeader, ModalContent, ModalFooter } from 'app/components/shared/Modal';
import { eventShape } from 'app/shapes/events';
import ContactPreferenceField from './ContactPreferenceField';
import EventDetails from './EventDetails';

const WAITLIST = 'waitlist';

export default function RegistrationForm({
  currentLibrary,
  currentUser,
  event,
  eventSeries,
  status,
  eventSettings,
  firstName,
  lastName,
  contactValue,
  contactType,
  onFirstNameChange,
  onLastNameChange,
  onContactValueChange,
  onContactTypeChange,
  numberAttending,
  onNumberAttendingChange,
  onClose,
  onSubmit
}) {
  const registrationInfo = event.getIn(['definition', 'registrationInfo']);
  const scope = registrationInfo.get('scope');
  const seatsRemaining = registrationInfo.get('cap') - event.get('numberRegistered');

  // Series date info needed for the series registration banner
  const firstEventKey = eventSeries.getIn(['eventKeys', 0])?.toString();
  const [seriesDate] = firstEventKey?.split('T') ?? [];
  const seriesStartDate = scope && moment(seriesDate).format('LL');
  const seriesStartDay = scope && moment(firstEventKey).format('dddd');

  let maxSeats = 1;
  if (status === WAITLIST) {
    maxSeats = Math.min(
      registrationInfo.get('cap'),
      registrationInfo.get('maxSeats'),
      eventSettings?.getIn(['eventRegistrationOptions', 'maxSeats'])
    );
  } else {
    maxSeats = Math.min(
      seatsRemaining,
      registrationInfo.get('cap'),
      registrationInfo.get('maxSeats'),
      eventSettings?.getIn(['eventRegistrationOptions', 'maxSeats'])
    );
  }
  const attendanceOptions = Array.from({ length: maxSeats }, (_, i) => i + 1);

  function handleEventRegistration() {
    onSubmit();
  }

  function renderLabel() {
    if (status === WAITLIST) {
      return <FormattedMessage defaultMessage="Join waitlist" id="join_waitlist" />;
    }
    return <FormattedMessage defaultMessage="Register" id="register" />;
  }

  return (
    <>
      <ModalHeader title={renderLabel()} />
      <ModalContent>
        <EventDetails event={event} eventSeries={eventSeries} />
        {scope && (
          <div className="series-info">
            <FormattedMessage
              id="events.registration.series.message"
              values={{ count: eventSeries.get('eventKeys')?.size, seriesStartDay, seriesStartDate }}
              defaultMessage={`You will be registered for all ${
                eventSeries.get('eventKeys')?.size
              } events in this series, beginning ${seriesStartDate}.`}
            />
          </div>
        )}
        <div className="registration-form-name">
          {currentUser?.getIn(['userContact', 'firstName']) ? (
            <div className="user-name">
              <label htmlFor="first-name">
                <FormattedMessage id="first_name" defaultMessage="First name" />
              </label>
              <div id="first-name">{firstName}</div>
            </div>
          ) : (
            <TextField
              className="name"
              label={<FormattedMessage id="first_name" defaultMessage="First name" />}
              id="reg_first_name"
              size="large"
              type="text"
              value={firstName}
              onChange={onFirstNameChange}
            />
          )}
          {currentUser?.getIn(['userContact', 'lastName']) ? (
            <div className="user-name">
              <label htmlFor="last-name">
                <FormattedMessage id="last_name" defaultMessage="Last name" />
              </label>
              <div id="last-name">{lastName}</div>
            </div>
          ) : (
            <TextField
              className="name"
              label={<FormattedMessage id="last_name" defaultMessage="Last name" />}
              id="reg_last_name"
              size="large"
              type="text"
              value={lastName}
              onChange={onLastNameChange}
            />
          )}
        </div>
        <div className="registration-form-contact">
          <ContactPreferenceField
            currentUser={currentUser}
            status={status}
            eventSettings={eventSettings}
            contactType={contactType}
            contactValue={contactValue}
            onContactTypeChange={onContactTypeChange}
            onContactValueChange={onContactValueChange}
          />
        </div>
        <div className="registration-form-attendance">
          <div className="section-title">
            <FormattedMessage id="number_attending" defaultMessage="Number of people attending" />
          </div>
          <Select
            id="attendance-select"
            dataKey="attendance-select"
            name="attendance"
            value={numberAttending}
            handleChange={onNumberAttendingChange}
            prompt=""
          >
            {attendanceOptions.map(i => (
              <SelectOption key={`seat-${i}`} value={i}>
                {i}
              </SelectOption>
            ))}
          </Select>
        </div>

        {status === WAITLIST && numberAttending > 1 && (
          <div className="attendee-alert">
            <span className="alert-icon">
              <Info />
            </span>
            <div className="alert-message">
              <FormattedHTMLMessage
                id="events.registration.attendee.alert_message"
                defaultMessage="Requesting more than one attendee decreases your chances of space becoming available."
              />
            </div>
          </div>
        )}

        {currentLibrary?.getIn(['events', 'eventsPrivacyDisclaimerEnabled']) && (
          <div className="privacy-link">
            <Link
              className="privacy-disclaimer"
              href={currentLibrary?.getIn(['events', 'eventsPrivacyDisclaimerUrl'])}
              dataKey="privacy-disclaimer-link"
              target="_blank"
            >
              <FormattedMessage
                id="events_privacy_disclaimer"
                defaultMessage="Learn how the library protects your privacy"
              />
            </Link>
          </div>
        )}
      </ModalContent>
      <ModalFooter
        disabled={!firstName || !lastName || !contactValue}
        primaryActionButtonText={renderLabel()}
        primaryActionButtonTag={TransactionalSolidButton}
        secondaryActionButtonText={<FormattedMessage defaultMessage="Cancel" id="button_cancel" />}
        handlePrimaryAction={handleEventRegistration}
        handleSecondaryAction={onClose}
        dataKey="events-registration-modal"
      />
    </>
  );
}

RegistrationForm.propTypes = {
  currentLibrary: libraryShape.isRequired,
  currentUser: userIdentityShape,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired,
  status: PropTypes.oneOf(['open', 'waitlist', 'closed']),
  eventSettings: ImmutablePropTypes.map,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  contactValue: PropTypes.string.isRequired,
  contactType: PropTypes.string.isRequired,
  onFirstNameChange: PropTypes.func.isRequired,
  onLastNameChange: PropTypes.func.isRequired,
  onContactValueChange: PropTypes.func.isRequired,
  onContactTypeChange: PropTypes.func.isRequired,
  numberAttending: PropTypes.number.isRequired,
  onNumberAttendingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};
